import "./dispute.css";
import { TabPanel, TabView } from "primereact/tabview";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { CustomLoader } from "../../../shared/components/custom-loader/custom-loader";
import { Icon } from "../../../shared/icons/icon";
import { SpecialLabelCases } from "../../../shared/models/utilities";
import { ResolveDispute } from "./resolve-dispute";
import { CustomModal } from "../../../shared/components/custom-modal/custom-modal";
import { useWindowSize } from "../../../core/custom-hooks/use-window-size";
import { MainContext } from "../../../../App";
import { ReopenDispute } from "./reopen-dispute";
import { HELPER } from "../../../shared/helper/helper";
import { SERVICES } from "../../../core/services/services";
import { ImageView } from "../../../shared/components/image-view/image-view";
import { Toast } from "primereact/toast";
import { DisputeRefundAttempts } from "./dispute-refund-attempts";
import { Optional } from "../../../shared/components/optional/optional";
import { DisputeAuditTrail } from "./dispute-audit-trail";
import {
  DECLINED,
  FAILED,
  PENDING,
  RESOLVED,
  SUCCESSFUL,
  TAT_EXPIRED,
  UserTypes,
} from "../../../shared/constants";
import { TransactionDetails } from "../../../shared/components/transactions/transaction-details";
import { MandateDetails } from "../../../shared/components/mandates/mandate-details";
import { CustomBreadcrumb } from "../../../shared/components/custom-breadcrumb/custom-breadcrumb";
import { AccountInformation } from "./account-information";

export function DisputeDetails(props) {
  const toast = useRef(null);
  const mainContext = useContext(MainContext);
  const [currentIndex, setCurrentIndex] = useState(1);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentModalIndex, setCurrentModalIndex] = useState(1);
  const [error, setError] = useState(null);
  const [detailsContent, setDetailsContent] = useState([]);
  const [countdown, setCountdown] = useState(null);
  const [hasDisputeTatElapsed, setHasDisputeTatElapsed] = useState();
  const [isDisputePending, setIsDisputePending] = useState(null);
  const [resolutionStatus, setResolutionStatus] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [isDisputeExpiryClose, setIsDisputeExpiryClose] = useState(false);
  const [showRefundAttempts, setShowRefundAttempts] = useState(false);
  const counter = useRef(null);
  const windowSize = useWindowSize();
  const [transactionReference, setTransactionReference] = useState(null);
  const [showDisputeAuditTrail, setShowDisputeAuditTrail] = useState(false);
  const [mandateReference, setMandateReference] = useState(null);
  const [loading, setLoading] = useState(false);
  const [disputeDetails, setDisputeDetails] = useState({});
  const [shouldReloadOnGoingBack, setShouldReloadOnGoingBack] = useState(false);

  function isTabMobile() {
    return windowSize.width <= 930;
  }

  const showTransactionDetails = useCallback((transactionReference) => {
    setTransactionReference(transactionReference);
    setCurrentIndex(5);
  }, []);

  const handleShowMandateDetails = (mandateReference) => {
    setMandateReference(mandateReference);
    setCurrentIndex(6);
  };

  const checkIfExpiryDateIsClose = useCallback((tatExpiryDate) => {
    let expiryHour = HELPER.GET_EXPIRY_HOUR(tatExpiryDate);
    let criticalHour = 5;
    if (expiryHour !== null && expiryHour <= criticalHour) {
      setIsDisputeExpiryClose(true);
    }
  }, []);

  const initalizeTatCountdown = useCallback((tatExpiryDate) => {
    let noCountdown = false;
    counter.current = setInterval(function () {
      if (noCountdown) {
        clearInterval(counter.current);
        return;
      }
      let countdown = HELPER.SET_COUNTDOWN(tatExpiryDate);
      if (countdown) {
        setCountdown(countdown);
      } else {
        noCountdown = true;
        clearInterval(counter);
        setHasDisputeTatElapsed(true);
      }
    }, 1000);

    if (noCountdown) {
      clearInterval(counter.current);
    }
  }, []);

  const getDisputeByLogCode = useCallback(() => {
    // setCurrentIndex(0);
    setIsModalVisible(false);
    setLoading(true);
    SERVICES.GET_DISPUTE_BY_LOG_CODE(props.logCode, props.userType)
      .then((data) => {
        let dispute = data?.result;
        setDisputeDetails(dispute);
        setDetailsContent(HELPER.GET_DISPUTE_DETAILS_ARRAY(dispute));
        setResolutionStatus(dispute?.resolutionStatus);
        const isDisputePending = dispute.status === PENDING;
        setIsDisputePending(isDisputePending);
        if (isDisputePending) {
          initalizeTatCountdown(dispute?.tatExpiryDate);
          checkIfExpiryDateIsClose(dispute?.tatExpiryDate);
        }
        setLoading(false);
        setCurrentIndex(1);
      })
      .catch((error) => {
        setError(HELPER.PROCESS_ERROR(error));
        toast.current.show({
          severity: "error",
          summary: "Error getting dispute details!",
          detail: HELPER.PROCESS_ERROR(error, "TOAST"),
          life: 10000,
        });
        setLoading(false);
        setCurrentIndex(1);
      });
  }, [
    checkIfExpiryDateIsClose,
    initalizeTatCountdown,
    props.logCode,
    props.userType,
  ]);

  useEffect(() => {
    return () => {
      clearInterval(counter.current);
    };
  }, []);

  useEffect(() => {
    getDisputeByLogCode();
  }, [getDisputeByLogCode]);

  function viewDisputeAuditTrail() {
    setShowDisputeAuditTrail(true);
  }

  const transformView = (itemCase, value) => {
    let result = value;
    if (itemCase) {
      SpecialLabelCases.forEach((e) => {
        if (e.case === itemCase) {
          result = e.action(value);
        }
      });
    }
    return result;
  };

  const canResolveDispute = () => {
    let canResolveDispute = false;
    const { userType } = props;
    const { transactionStatus, status, loggedBy, reopenedBy } = disputeDetails;
    const loggedInUser = mainContext?.mainState?.username;
    if (
      loggedBy === loggedInUser ||
      reopenedBy === loggedInUser ||
      status === RESOLVED
    ) {
      canResolveDispute = false;
      return canResolveDispute;
    }
    if (userType === UserTypes.Backoffice) {
      if (
        transactionStatus === FAILED ||
        (transactionStatus === SUCCESSFUL && status === TAT_EXPIRED)
      ) {
        canResolveDispute = true;
      }
    } else if (userType === UserTypes.Frontoffice) {
      if (transactionStatus === SUCCESSFUL && status === PENDING) {
        canResolveDispute = true;
      }
    }
    return canResolveDispute;
  };

  const showResolveButton = () => {
    if (canResolveDispute()) {
      return (
        <button onClick={() => openModal(1)} className="primary-button p-mr-3">
          Resolve
        </button>
      );
    }
  };

  const canReopenDispute = () => {
    const { userType } = props;
    const { resolutionStatus, transactionStatus, status } = disputeDetails;
    let canReopenDispute = false;

    if (
      userType === UserTypes.Frontoffice &&
      transactionStatus === SUCCESSFUL &&
      resolutionStatus === DECLINED
    ) {
      canReopenDispute = true;
    }
    if (
      userType === UserTypes.Backoffice &&
      (status === TAT_EXPIRED || resolutionStatus === DECLINED)
    ) {
      canReopenDispute = true;
    }
    return canReopenDispute;
  };

  const showReopenButton = () => {
    if (canReopenDispute()) {
      return (
        <button onClick={() => openModal(2)} className="tertiary-button">
          Reopen
        </button>
      );
    } else {
      return <div />;
    }
  };

  function handleGoBack() {
    if (currentIndex > 1 || showRefundAttempts || showDisputeAuditTrail) {
      setShowRefundAttempts(false);
      setShowDisputeAuditTrail(false);
      setCurrentIndex(1);
    } else {
      props.goBack(shouldReloadOnGoingBack);
    }
  }

  function onHide() {}

  function closeModal(reload) {
    if (props.mobile) {
      setCurrentIndex(1);
    } else {
      setIsModalVisible(false);
    }
    if (reload === true) {
      getDisputeByLogCode();
    }
  }

  function openModal(index) {
    if (props.mobile) {
      setCurrentIndex(3);
    } else {
      setCurrentModalIndex(index);
      setIsModalVisible(true);
    }
  }

  function getAuditTrail() {
    if (disputeDetails?.statusLogInfo) {
      return JSON.parse(disputeDetails?.statusLogInfo);
    } else {
      return null;
    }
  }

  function viewAccountInformation() {
    openModal(3);
  }

  function getBreadcrumbTitle() {
    if (currentIndex === 5) {
      return "Transaction";
    } else if (currentIndex === 6) {
      return "Mandate";
    }
    return "Dispute";
  }

  function onActionTakenOnDispute() {
    setShouldReloadOnGoingBack(true);
    getDisputeByLogCode();
  }

  const modalContent = () => {
    switch (currentModalIndex) {
      case 1:
        return (
          <ResolveDispute
            logCode={props.logCode}
            closeModal={closeModal}
            userType={props.userType}
            disputeType={props.disputeType}
            onDisputeResolved={onActionTakenOnDispute}
          />
        );
      case 2:
        return (
          <ReopenDispute
            onDisputeReopened={onActionTakenOnDispute}
            closeModal={closeModal}
            logCode={props.logCode}
            userType={props.userType}
            disputeStatus={disputeDetails?.status}
          />
        );
      case 3:
        return (
          <AccountInformation
            accountNumber={disputeDetails?.customerAccountNumber}
            closeModal={closeModal}
          />
        );
      default:
        break;
    }
  };

  const detailsView = detailsContent.map((detail, index) => (
    <div key={index.toString()} className="p-grid dcir-row">
      <div className="p-col-6">
        <p className="details-label">{detail.label}:</p>
      </div>
      <div className="p-col-6">
        <div>
          <Optional show={detail.action === "viewTransaction"}>
            <p
              className="details-value cursor-pointer underline"
              style={{ color: "#0371A0" }}
              onClick={() => showTransactionDetails(detail.value)}
            >
              {transformView(detail.itemCase, detail.value)}
            </p>
          </Optional>
          <Optional show={detail.action === "viewMandate"}>
            <p
              className={`details-value cursor-pointer ${
                HELPER.HAS_AUTHORITY("bo_view_debit_mandates") ||
                HELPER.HAS_AUTHORITY("fo_view_debit_mandates")
                  ? "underline"
                  : ""
              }`}
              style={{
                color: `${
                  HELPER.HAS_AUTHORITY("bo_view_debit_mandates") ||
                  HELPER.HAS_AUTHORITY("fo_view_debit_mandates")
                    ? "#0371A0"
                    : ""
                }`,
              }}
              onClick={() => {
                if (
                  HELPER.HAS_AUTHORITY("bo_view_debit_mandates") ||
                  HELPER.HAS_AUTHORITY("fo_view_debit_mandates")
                ) {
                  handleShowMandateDetails(detail.value);
                }
              }}
            >
              {transformView(detail.itemCase, detail.value)}
            </p>
          </Optional>
          <Optional show={!detail.action}>
            <p className="details-value">
              {transformView(detail.itemCase, detail.value)}
            </p>
          </Optional>
        </div>
        <Optional
          show={
            detail.itemCase === "resolutionStatus" &&
            disputeDetails?.statusLogInfo
          }
        >
          <button
            className="p-button-success view-details"
            onClick={viewDisputeAuditTrail}
          >
            View Details
          </button>
        </Optional>
        <Optional show={detail.itemCase === "customerAccountNumber"}>
          <button
            className="p-button-success view-details"
            onClick={() => viewAccountInformation(detail.value)}
          >
            Name Enquiry
          </button>
        </Optional>
      </div>
    </div>
  ));

  const errorView = () => {
    return (
      <div>
        <div className="empty-container">
          <Icon icon="error-message-icon" />
          <div>
            <p className="empty-text">{error}</p>
          </div>
          <div className="p-mt-3">
            <div style={{ maxWidth: "200px" }}>
              <button
                onClick={() => getDisputeByLogCode()}
                className="primary-button"
              >
                Reload
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const disputeTabView = () => {
    return (
      <div
        style={{ backgroundColor: "#ffffff", paddingTop: "1em" }}
        className="p-shadow-1 p-pb-5"
      >
        <div className="p-text-left p-ml-3 p-mt-2 p-pb-3">
          <span onClick={handleGoBack} className="add-cursor">
            <span>
              <Icon icon="go-back-icon" />
            </span>
          </span>
        </div>
        <TabView>
          <TabPanel
            header={
              showRefundAttempts
                ? "Dispute Refund Attempts"
                : showDisputeAuditTrail
                ? "Dispute Status Log"
                : "Dispute Details"
            }
          >
            <Optional show={loading}>
              <div style={{ margin: "10rem 0" }}>
                <CustomLoader loadingText="Loading dispute details ..." />
              </div>
            </Optional>

            <Optional show={!loading}>
              <Optional show={showRefundAttempts}>
                <DisputeRefundAttempts
                  transactionReference={disputeDetails?.transactionReference}
                  requestType={props.userType}
                />
              </Optional>
              <Optional
                show={showDisputeAuditTrail && disputeDetails?.statusLogInfo}
              >
                <DisputeAuditTrail disputeAuditTrail={getAuditTrail()} />
              </Optional>
              <Optional show={!showRefundAttempts && !showDisputeAuditTrail}>
                <div className="p-text-left">
                  <div className="p-grid p-mt-2">
                    <div className="p-col-8" />
                    <div className="p-col-4">
                      <Optional
                        show={
                          (HELPER.HAS_AUTHORITY("bo_dd_update_dispute") ||
                            HELPER.HAS_AUTHORITY("fo_dd_update_dispute")) &&
                          !loading
                        }
                      >
                        <div style={{ width: "135px", float: "right" }}>
                          {showResolveButton()}
                        </div>
                      </Optional>

                      <Optional
                        show={
                          (HELPER.HAS_AUTHORITY("bo_dd_reopen_dispute") ||
                            HELPER.HAS_AUTHORITY("fo_dd_reopen_dispute")) &&
                          !loading &&
                          (resolutionStatus === DECLINED ||
                            disputeDetails?.status === TAT_EXPIRED)
                        }
                      >
                        <div
                          style={{ width: "135px", float: "right" }}
                          className="p-mr-4"
                        >
                          {showReopenButton()}
                        </div>
                      </Optional>
                    </div>
                  </div>
                  <Optional show={isDisputePending && countdown}>
                    <div className="p-col-12">
                      <div className="info-card">
                        <div className="dcir-show dispute-lodged-title">
                          {/* <span>TAT Countdown: {countdown}</span> */}
                        </div>
                        <div className="p-mt-3">
                          <span
                            className={`info-card-text ${
                              isDisputeExpiryClose || hasDisputeTatElapsed
                                ? "p-error"
                                : ""
                            }`}
                          >
                            {hasDisputeTatElapsed
                              ? "Turn around time has elapsed!"
                              : `Expires in: ${countdown}`}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Optional>

                  <div>
                    <div className="centralize">
                      {detailsView}
                      <Optional
                        show={
                          disputeDetails?.receiptData &&
                          resolutionStatus === "DECLINED"
                        }
                      >
                        <div className="p-grid dcir-row">
                          <div className="p-col-6">
                            <p className="details-label">Receipt:</p>
                          </div>
                          <div className="p-col-6">
                            <p className="details-value">
                              <button
                                className="view-receipt bare-button"
                                onClick={() => setShowReceipt(true)}
                              >
                                View
                              </button>
                            </p>
                          </div>
                        </div>
                      </Optional>
                    </div>
                  </div>
                </div>
              </Optional>
            </Optional>
          </TabPanel>
        </TabView>
      </div>
    );
  };

  const renderDisputeDetails = () => {
    // eslint-disable-next-line default-case
    switch (currentIndex) {
      case 0:
        return (
          <div className="p-mt-5">
            <CustomLoader loadingText="Loading dispute details ..." />
          </div>
        );
      case 1:
        return disputeTabView();
      case 2:
        return errorView();
      case 3:
        return (
          <div className="custom-mobile-table-card">
            <ResolveDispute
              onDisputeResolved={onActionTakenOnDispute}
              logCode={props.logCode}
              closeModal={closeModal}
              userType={props.userType}
            />
          </div>
        );
      case 4:
        return (
          <DisputeRefundAttempts
            disputeId={props.disputeId}
            goBack={() => setCurrentIndex(1)}
            requestType={props.userType}
          />
        );
      case 5:
        return (
          <TransactionDetails
            transactionReference={transactionReference}
            goBack={() => setCurrentIndex(1)}
            mobile={isTabMobile()}
            userType={props.userType}
            isDisputePage={true}
            onShowMandateDetails={() => {}}
            onHideMandateDetails={() => {}}
            // toggleBreadCrumbView={toggleBreadCrumbView}
          />
        );
      case 6:
        return (
          <MandateDetails
            mandateReference={mandateReference}
            userType={props.userType}
            goBack={() => setCurrentIndex(1)}
          />
        );
    }
  };

  return (
    <div>
      <div>
        <Toast ref={toast} />
      </div>
      <div>
        <CustomModal
          closeModal={closeModal}
          onHide={onHide}
          visible={isModalVisible}
          modalContent={modalContent}
        />
      </div>
      <Optional show={currentIndex !== 5}>
        <div className="page-title p-text-left">{getBreadcrumbTitle()}</div>
        <div className="p-mb-5">
          <CustomBreadcrumb page={`Manage ${getBreadcrumbTitle()}`} />
        </div>
      </Optional>
      <Optional show={showReceipt}>
        <ImageView
          image={disputeDetails?.receiptData}
          logCode={disputeDetails?.logCode}
          close={() => setShowReceipt(false)}
        />
      </Optional>
      {renderDisputeDetails()}
    </div>
  );
}
